/**
 * Route history helper for extra views (preview/redeem flows).
 * Keeps route history in Vuex only (no Capacitor persistence).
 */

/**
 * No-op: route history is no longer persisted to storage.
 */
export async function persistRouteHistory() {
  // Route history kept in Vuex only
}

/**
 * Set route history in Vuex and persist to storage.
 * Use from PreviewEvent, PreviewGift, RedeemGift, etc. before redirecting.
 * @param {import('vuex').Store} store
 * @param {string[]} routes - Array of full paths, e.g. ['/events', '/event/1', '/individual/gift/2']
 */
export async function setRouteHistory(store, routes) {
  if (!store || !Array.isArray(routes)) return;
  store.dispatch('navigation/setRouteHistory', routes);
  await persistRouteHistory(store);
}

/**
 * Apply route history to the browser's history stack (replaceState + pushState)
 * so that back button behavior matches the given routes. Does not navigate.
 * @param {string[]} routeHistory - Array of full paths
 * @param {string} base - Router base, e.g. ''
 * @returns {function(string): string} path helper
 */
export function getPathHelper(base = '') {
  return (p) => (base + p).replace(/\/+/g, '/');
}

/**
 * Update browser history stack to match routeHistory (without triggering navigation).
 * Call after setRouteHistory and before router.push(destination).
 * @param {string[]} routeHistory - Array of full paths (length >= 1)
 * @param {string} [base=''] - Router base
 */
export function applyHistoryToBrowser(routeHistory, base = '') {
  if (!routeHistory?.length) return;
  const path = getPathHelper(base);
  history.replaceState({ ...history.state }, '', path(routeHistory[0]));
  for (let i = 1; i < routeHistory.length; i++) {
    history.pushState({ ...history.state }, '', path(routeHistory[i]));
  }
}

/**
 * Set route history in Vuex, persist, apply to browser, then navigate to the last route.
 * Use in extra views when redirecting after gift/event creation or redeem.
 * @param {import('vuex').Store} store
 * @param {string[]} routeHistory - Full stack, e.g. ['/events', '/event/1', '/individual/gift/2']
 * @param {string} [base=''] - Router base
 * @param {import('vue-router').Router} router - Vue Router instance (for push)
 */
export async function setRouteHistoryAndNavigate(store, routeHistory, base = '', router) {
  if (!store || !routeHistory?.length || !router) return;
  await setRouteHistory(store, routeHistory);
  applyHistoryToBrowser(routeHistory, base);
  router.push(routeHistory[routeHistory.length - 1]);
}
